import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Users } from "lucide-react";
import { normalizeSsoEmail } from "../../utils/recruiterSsoFilter";

/**
 * Same User (SSO) multi-select used on Analytics.
 * Empty selection = All users.
 */
export default function RecruiterSsoDropdown({
  recruiters = [],
  selectedEmails,
  onChange,
  disabled = false,
  label = "User (SSO)",
  compact = false,
  icon: Icon = Users,
  allLabel = "All Users",
  itemNoun = "users",
  emptyLabel = "No users found",
  showDetail = true,
  accent = "#059669",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = selectedEmails instanceof Set ? selectedEmails : new Set();

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectedNormalized = new Set(Array.from(selected).map(normalizeSsoEmail));
  const recruiterLabel =
    selectedNormalized.size === 0
      ? allLabel
      : selectedNormalized.size === 1
        ? (recruiters.find((r) => selectedNormalized.has(normalizeSsoEmail(r.email)))?.name || "1 selected")
        : `${selectedNormalized.size} ${itemNoun}`;

  const toggle = (email) => {
    const key = normalizeSsoEmail(email);
    const next = new Set(Array.from(selected).map(normalizeSsoEmail));
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  const clear = () => onChange(new Set());

  return (
    <div ref={wrapRef} style={{ flex: compact ? "0 0 auto" : "1 1 200px", minWidth: compact ? 170 : 170 }}>
      <div style={{
        fontSize: compact ? 11 : 12,
        fontWeight: 700,
        color: "var(--ds-text2)",
        marginBottom: compact ? 4 : 6,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}>
        <Icon size={compact ? 12 : 14} /> {label}
      </div>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          style={{
            background: selected.size > 0 ? `${accent}12` : "var(--ds-input-bg)",
            border: `1.5px solid ${selected.size > 0 ? `${accent}80` : "var(--ds-border)"}`,
            borderRadius: 8,
            color: "var(--ds-text)",
            padding: compact ? "6px 8px 6px 10px" : "8px 10px 8px 12px",
            fontSize: compact ? 12 : 13,
            outline: "none",
            width: "100%",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: disabled ? "not-allowed" : "pointer",
            textAlign: "left",
          }}
        >
          <span style={{
            fontSize: compact ? 12 : 13,
            color: selected.size > 0 ? accent : "var(--ds-muted)",
            fontWeight: selected.size > 0 ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {recruiterLabel}
          </span>
          <ChevronDown size={14} style={{ color: "var(--ds-muted)", flexShrink: 0, transition: "transform 0.18s", transform: open ? "rotate(180deg)" : "none" }} />
        </button>

        {open && (
          <div style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: compact ? 0 : "auto",
            left: compact ? "auto" : 0,
            zIndex: 9999,
            background: "var(--ds-card)",
            border: "1.5px solid var(--ds-border)",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            minWidth: compact ? 240 : "100%",
            maxWidth: 280,
            maxHeight: 240,
            overflowY: "auto",
          }}>
            {selected.size > 0 && (
              <button
                type="button"
                onClick={clear}
                style={{
                  display: "block", width: "100%", padding: "8px 14px",
                  background: "transparent", border: "none",
                  borderBottom: "1px solid var(--ds-border)", cursor: "pointer",
                  textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ef4444",
                  fontFamily: "inherit",
                }}
              >
                ✕ Clear selection
              </button>
            )}
            {recruiters.length === 0 ? (
              <div style={{ padding: "12px 14px", color: "var(--ds-muted)", fontSize: 12 }}>{emptyLabel}</div>
            ) : recruiters.map((r) => {
              const isChecked = selectedNormalized.has(normalizeSsoEmail(r.email));
              return (
                <button
                  type="button"
                  key={r.email}
                  onClick={() => toggle(r.email)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "9px 14px",
                    background: isChecked ? `${accent}14` : "transparent",
                    border: "none", borderBottom: "1px solid var(--ds-border)",
                    cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.background = "var(--ds-hover)"; }}
                  onMouseLeave={(e) => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: `2px solid ${isChecked ? accent : "var(--ds-border)"}`,
                    background: isChecked ? accent : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "all 0.15s",
                  }}>
                    {isChecked && (
                      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ds-text)", lineHeight: 1.3 }}>{r.name}</div>
                    {showDetail ? (
                      <div style={{ fontSize: 10, color: "var(--ds-muted)" }}>{r.email}</div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
